/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import {
    LOCATION_ERROR,
    LOCATION_CLEAR_ERROR,
    LOCATION_CLEAR_SUCCESS,
    LOCATION_RESET,
    LOCATION_SAVE_START,
    LOCATION_SAVE_SUCCESS,
    LOCATION_FETCH_START,
    LOCATION_FETCH_SUCCESS,
    LOCATIONS_FETCH_SUCCESS,
    LocationAction
} from './LocationActionsTypes';
import {
    convertToSaveLocationDto,
    convertToLocation,
    convertToLocationStaffMember,
    convertToDuplicateLocationRequestDto,
    convertToLocationDuplication
} from './LocationConverter';
import {
    SaveLocation,
    Location,
    LocationPagination,
    LocationStaffFilters,
    LocationStaffPage,
    DuplicateLocationDetails,
    LocationCopyArea,
    LocationDuplication
} from 'model/location';
import {
    FetchLocationsDto,
    FetchLocationStaffDto,
    DuplicateLocationResponseDto,
    LocationDuplicationDto
} from './LocationDtos';

const marketplacesUrl = '/marketplaces';

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like {"name":["has already been taken"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                // Extract first error from validation errors object
                const firstField = Object.keys(data.errors)[0];
                const firstError = data.errors[firstField];
                if (Array.isArray(firstError)) {
                    return firstError[0];
                }
                return firstError;
            }
            return data.errors;
        }

        // Check for general error message
        if (data.message) {
            return data.message;
        }

        // Check for error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to Axios message or generic error
    if (apiError?.message) {
        return apiError.message;
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
};

const error = (err: string): LocationAction => {
    return {
        type: LOCATION_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearLocationError = (): LocationAction => {
    return {
        type: LOCATION_CLEAR_ERROR
    };
};

export const clearLocationSuccess = (): LocationAction => {
    return {
        type: LOCATION_CLEAR_SUCCESS
    };
};

export const resetLocationState = (): LocationAction => {
    return {
        type: LOCATION_RESET
    };
};

export const saveLocationThunk = (
    location: SaveLocation
): ThunkAction<Promise<any>, RootState, null, LocationAction> => {
    return async dispatch => {
        dispatch({ type: LOCATION_SAVE_START });

        try {
            // Convert SaveLocation model to SaveLocationDto (API payload format)
            const payload = convertToSaveLocationDto(location);

            // If editing existing location (has id), do a single PATCH with all data
            if (location.id) {
                const url = `${marketplacesUrl}/${location.id}?marketplace_id=${location.id}`;
                const { data }: AxiosResponse<any> = await ApiClient.patch(url, payload);
                const savedLocation = convertToLocation(data);

                dispatch({
                    type: LOCATION_SAVE_SUCCESS,
                    payload: {
                        location: savedLocation
                    }
                });

                return savedLocation;
            }

            // For new location: Two-step flow
            // Step 1: POST without addresses_attributes
            const { addresses_attributes, ...payloadWithoutAddress } = payload;
            const { data: postData }: AxiosResponse<any> = await ApiClient.post(marketplacesUrl, payloadWithoutAddress);
            const newLocationId = postData.id;

            // Step 2: PATCH with addresses_attributes if address data exists
            if (addresses_attributes && addresses_attributes.length > 0) {
                const patchUrl = `${marketplacesUrl}/${newLocationId}?marketplace_id=${newLocationId}`;
                // Update marketplace_id in addresses_attributes with the new location id
                const updatedAddresses = addresses_attributes.map(address => ({
                    ...address,
                    marketplace_id: newLocationId
                }));
                const addressPayload = { addresses_attributes: updatedAddresses };
                const { data: patchData }: AxiosResponse<any> = await ApiClient.patch(patchUrl, addressPayload);
                const savedLocation = convertToLocation(patchData);

                dispatch({
                    type: LOCATION_SAVE_SUCCESS,
                    payload: {
                        location: savedLocation
                    }
                });

                return savedLocation;
            }

            // If no address data, just return the created location
            const savedLocation = convertToLocation(postData);
            dispatch({
                type: LOCATION_SAVE_SUCCESS,
                payload: {
                    location: savedLocation
                }
            });

            return savedLocation;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

export const fetchLocationThunk = (
    locationId: number
): ThunkAction<Promise<Location>, RootState, null, LocationAction> => {
    return async dispatch => {
        dispatch({ type: LOCATION_FETCH_START });

        try {
            const url = `${marketplacesUrl}/${locationId}`;
            const { data }: AxiosResponse<any> = await ApiClient.get(url, {
                params: {
                    marketplace_id: locationId
                }
            });

            // Convert the DTO response to Location model
            const location = convertToLocation(data);

            dispatch({
                type: LOCATION_FETCH_SUCCESS,
                payload: {
                    location
                }
            });

            return location;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

export const fetchLocationsThunk = (
    page?: number,
    size?: number,
    name?: string
): ThunkAction<Promise<Location[]>, RootState, null, LocationAction> => {
    return async (dispatch, getState) => {
        dispatch({ type: LOCATION_FETCH_START });

        try {
            // Get user_id from Redux state
            const state = getState();
            const userId = state.login?.auth?.user?.id;

            if (!userId) {
                throw new Error('User ID not found. Please log in again.');
            }

            const url = `organization_marketplaces/${userId}`;
            const { data }: AxiosResponse<FetchLocationsDto> = await ApiClient.get(url, {
                params: {
                    page: page ?? undefined,
                    size: size ?? undefined,
                    name: name ?? undefined
                }
            });

            // Convert the DTO response to Location model array
            const locations = Array.isArray(data.marketplaces) ? data.marketplaces.map(convertToLocation) : [];

            const pagination: LocationPagination = {
                page: data.page,
                size: data.size,
                totalPages: data.total_pages,
                totalRecords: data.total_records
            };

            dispatch({
                type: LOCATIONS_FETCH_SUCCESS,
                payload: {
                    locations,
                    pagination
                }
            });

            return locations;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

/**
 * Fetches one page of the staff of a location. Errors are thrown to the caller untouched.
 */
export const fetchLocationStaff = async (
    locationId: number,
    filters: LocationStaffFilters
): Promise<LocationStaffPage> => {
    const url = `organization_marketplaces/${locationId}/staffs`;
    const { data }: AxiosResponse<FetchLocationStaffDto> = await ApiClient.get(url, {
        params: {
            status: filters.status,
            name: filters.name || undefined,
            page: filters.page,
            size: filters.size
        }
    });

    return {
        staff: Array.isArray(data.staffs) ? data.staffs.map(convertToLocationStaffMember) : [],
        pagination: {
            page: data.page,
            size: data.size,
            totalPages: data.total_pages,
            totalRecords: data.total_records
        }
    };
};

/**
 * Starts duplicating a location in the background. Resolves to the duplication id. Errors are thrown to the caller untouched.
 */
export const startLocationDuplication = async (
    locationId: number,
    details: DuplicateLocationDetails,
    areas: Array<LocationCopyArea>
): Promise<string> => {
    const url = `organization_marketplaces/${locationId}/duplicate`;
    const { data }: AxiosResponse<DuplicateLocationResponseDto> = await ApiClient.post(
        url,
        convertToDuplicateLocationRequestDto(details, areas)
    );

    return data.duplication_id;
};

/**
 * Fetches the status of a location duplication. Errors are thrown to the caller untouched.
 */
export const fetchLocationDuplication = async (duplicationId: string): Promise<LocationDuplication> => {
    const url = `organization_marketplaces/duplications/${duplicationId}`;
    const { data }: AxiosResponse<LocationDuplicationDto> = await ApiClient.get(url);

    return convertToLocationDuplication(data);
};
